import { useState } from "react";
import { Eye, EyeOff, Mail, Lock, Package, ShieldCheck } from "lucide-react";
import { useDistributorSignin } from "../hooks/useDistributorSignin";

const DistributorSignin = () => {
  const { form, submitting, error, handleChange, handleSubmit } =
    useDistributorSignin();
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  return (
    <div className="signin-container">
      <div className="signin-background">
        <div className="background-pattern"></div>
        <div className="background-overlay"></div>
      </div>

      <div className="signin-content">
        <div className="signin-card">
          {/* Branding Header */}
          <div className="signin-header">
            <div className="brand-container">
              <div className="brand-icon">
                <Package size={32} />
              </div>
              <div className="brand-text">
                <h1 className="brand-title">
                  {process.env.REACT_APP_CLIENT_NAME || "Store Admin"}
                </h1>
                <p className="brand-subtitle">Management Portal</p>
              </div>
            </div>
            <div className="security-badge">
              <ShieldCheck size={16} />
              <span>Secure Login</span>
            </div>
          </div>

          {/* Welcome Message */}
          <div className="welcome-section">
            <h2 className="welcome-title">Welcome back</h2>
            <p className="welcome-text">
              Sign in to access your admin dashboard and manage your store
              operations.
            </p>
          </div>

          {/* Sign In Form */}
          <form onSubmit={handleSubmit} className="signin-form">
            {error && (
              <div className="alert alert-error" role="alert">
                <div className="alert-content">
                  <div className="alert-icon">⚠️</div>
                  <div className="alert-message">{error}</div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="email">
                Email Address
              </label>
              <div className="input-container">
                <Mail size={18} className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <div className="input-container">
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className="form-input"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="signin-button"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="spinner"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <span className="button-arrow">→</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="signin-footer">
            <p className="footer-text">
              Secure and encrypted connection protected by industry-standard
              security.
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        .signin-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          background: linear-gradient(
            135deg,
            var(--primary-50) 0%,
            var(--primary-100) 100%
          );
          padding: 1rem;
        }

        .signin-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
        }

        .background-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="pattern" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1.5" fill="rgba(59, 130, 246, 0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23pattern)"/></svg>');
        }

        .background-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            rgba(59, 130, 246, 0.05) 0%,
            rgba(99, 102, 241, 0.05) 100%
          );
        }

        .signin-content {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 420px;
        }

        .signin-card {
          background: white;
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-xl);
          border: 1px solid var(--gray-200);
          overflow: hidden;
          backdrop-filter: blur(20px);
        }

        .signin-header {
          padding: 2rem 2rem 1rem;
          background: linear-gradient(135deg, var(--gray-50) 0%, white 100%);
          border-bottom: 1px solid var(--gray-100);
        }

        .brand-container {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .brand-icon {
          width: 56px;
          height: 56px;
          background: linear-gradient(
            135deg,
            var(--primary-500) 0%,
            var(--primary-600) 100%
          );
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: var(--shadow-md);
        }

        .brand-text {
          flex: 1;
        }

        .brand-title {
          margin: 0 0 0.25rem 0;
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
        }

        .brand-subtitle {
          margin: 0;
          font-size: 0.875rem;
          color: var(--gray-600);
        }

        .security-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--success-50);
          color: var(--success-600);
          border: 1px solid var(--success-200);
          border-radius: var(--radius-md);
          font-size: 0.75rem;
          font-weight: 500;
        }

        .welcome-section {
          padding: 1.5rem 2rem;
          text-align: center;
        }

        .welcome-title {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
        }

        .welcome-text {
          margin: 0;
          color: var(--gray-600);
          line-height: 1.5;
        }

        .signin-form {
          padding: 0 2rem 2rem;
        }

        .alert {
          margin-bottom: 1.5rem;
          padding: 1rem;
          border-radius: var(--radius-md);
          border: 1px solid transparent;
        }

        .alert-error {
          background-color: var(--error-50);
          border-color: var(--error-200);
          color: var(--error-700);
        }

        .alert-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .alert-icon {
          font-size: 1.125rem;
        }

        .alert-message {
          flex: 1;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: var(--gray-700);
          font-size: 0.875rem;
        }

        .input-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          color: var(--gray-400);
          z-index: 1;
        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 3rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--radius-md);
          background-color: white;
          font-size: 0.875rem;
          transition: all var(--transition-fast);
        }

        .form-input:focus {
          outline: none;
          border-color: var(--primary-500);
          box-shadow: 0 0 0 3px var(--primary-100);
        }

        .form-input::placeholder {
          color: var(--gray-400);
        }

        .password-toggle {
          position: absolute;
          right: 1rem;
          background: none;
          border: none;
          color: var(--gray-400);
          cursor: pointer;
          padding: 0.25rem;
          border-radius: var(--radius-sm);
          transition: color var(--transition-fast);
        }

        .password-toggle:hover {
          color: var(--gray-600);
        }

        .signin-button {
          width: 100%;
          padding: 0.875rem 1.5rem;
          background: linear-gradient(
            135deg,
            var(--primary-600) 0%,
            var(--primary-700) 100%
          );
          color: white;
          border: none;
          border-radius: var(--radius-md);
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all var(--transition-fast);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1rem;
        }

        .signin-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: var(--shadow-lg);
        }

        .signin-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .button-arrow {
          font-size: 1.125rem;
          transition: transform var(--transition-fast);
        }

        .signin-button:hover .button-arrow {
          transform: translateX(2px);
        }

        .signin-footer {
          padding: 1.5rem 2rem;
          background: var(--gray-50);
          border-top: 1px solid var(--gray-100);
          text-align: center;
        }

        .footer-text {
          margin: 0;
          font-size: 0.75rem;
          color: var(--gray-500);
          line-height: 1.4;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 480px) {
          .signin-container {
            padding: 0.5rem;
          }

          .signin-header,
          .welcome-section,
          .signin-form,
          .signin-footer {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }

          .brand-title {
            font-size: 1.125rem;
          }

          .welcome-title {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
};

export default DistributorSignin;
